import { ControlledCheckbox } from '@if/mui'
import { Box, Stack, Typography } from '@mui/material'
import { OwnershipSwitch } from '@src/components'
import { BannerDto } from '@src/data/types/BannerSchema'
import { EBannerOwnedFieldKey } from '@src/data/types/Constants'
import { memo, useCallback, useMemo } from 'react'
import { Control, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const ownershipConfig = [
  {
    fieldKey: EBannerOwnedFieldKey.NoPmtPolicy,
    formFieldName: 'noPaymentPolicy' as const,
    titleKey: 'merchant.noPaymentPolicy.title',
    descriptionKey: 'merchant.noPaymentPolicy.description',
  },
  {
    fieldKey: EBannerOwnedFieldKey.WorksheetByMerchant,
    formFieldName: 'worksheetByMerchant' as const,
    titleKey: 'merchant.worksheetByMerchant.title',
    descriptionKey: 'merchant.worksheetByMerchant.description',
  },
  {
    fieldKey: EBannerOwnedFieldKey.HideAddress,
    formFieldName: 'hideAddress' as const,
    titleKey: 'merchant.hideAddress.title',
    descriptionKey: 'merchant.hideAddress.description',
  },
  {
    fieldKey: EBannerOwnedFieldKey.SkipIncomeVerificationFlinks,
    formFieldName: 'canSkipFlinks' as const,
    titleKey: 'merchant.canSkipFlinks.title',
    descriptionKey: 'merchant.canSkipFlinks.description',
  },
  {
    fieldKey: EBannerOwnedFieldKey.CanPayBorrowers,
    formFieldName: 'canPayBorrowers' as const,
    titleKey: 'merchant.canPayBorrowers.title',
    descriptionKey: 'merchant.canPayBorrowers.description',
  },
  {
    fieldKey: EBannerOwnedFieldKey.AutoIncomeApproval,
    formFieldName: 'automaticIncomeApproval' as const,
    titleKey: 'merchant.automaticIncomeApproval.title',
    descriptionKey: 'merchant.automaticIncomeApproval.description',
  },
  {
    fieldKey: EBannerOwnedFieldKey.CanUseHighEstimate,
    formFieldName: 'canAcceptHighEstimate' as const,
    titleKey: 'merchant.canAcceptHighEstimate.title',
    descriptionKey: 'merchant.canAcceptHighEstimate.description',
  },
  {
    fieldKey: EBannerOwnedFieldKey.AlwaysShowInterestRateMerchDb,
    formFieldName: 'showInterestRateOnMerchantDashboard' as const,
    titleKey: 'merchant.showInterestRate.title',
    descriptionKey: 'merchant.showInterestRate.description',
  },
] as const

interface Props {
  control: Control<BannerDto>
}

const BannerPropertiesOwnershipPanel = ({ control }: Props) => {
  const { t } = useTranslation()
  const { setValue } = useFormContext<BannerDto>()
  const ownedFields = useWatch({ control, name: 'ownedFields', defaultValue: [] })

  const handleOwnershipChange = useCallback(
    (fieldKey: string, newOwnership: 'banner' | 'merchant') => {
      const newOwnedFields =
        newOwnership === 'banner' ? [...ownedFields, fieldKey] : ownedFields.filter((f: string) => f !== fieldKey)
      setValue('ownedFields', newOwnedFields, { shouldDirty: true })
    },
    [ownedFields, setValue],
  )

  const rows = useMemo(
    () =>
      ownershipConfig.map(({ fieldKey, formFieldName, titleKey, descriptionKey }) => {
        const isBannerOwned = ownedFields.includes(fieldKey)
        return (
          <Stack
            key={fieldKey}
            direction="row"
            alignItems="center"
            gap={2}
            sx={{ borderRadius: 1, px: 1, py: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
          >
            <OwnershipSwitch
              isBannerOwned={isBannerOwned}
              onChange={(ownership) => handleOwnershipChange(fieldKey, ownership)}
            />
            <ControlledCheckbox
              name={formFieldName}
              control={control}
              disabled={!isBannerOwned}
              label={
                <Box>
                  <Typography>{t(titleKey)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(descriptionKey)}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        )
      }),
    [ownedFields, control, t, handleOwnershipChange],
  )

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('banner.propertiesOwnership')}
      </Typography>
      <Stack gap={1}>{rows}</Stack>
    </Box>
  )
}

export default memo(BannerPropertiesOwnershipPanel)
